import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
} from '@mui/material';

const PRIMARY = '#154A8A';

const dialogPaperSx = {
  '& .MuiDialog-paper': {
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    p: 2,
  },
};

const titleSx = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: PRIMARY,
  pb: 1,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
};

const secondaryBtnSx = {
  borderColor: 'grey.400',
  color: 'grey.700',
  '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
};

const primaryBtnSx = {
  bgcolor: PRIMARY,
  '&:hover': { bgcolor: '#0d3a6a' },
  '&:disabled': { bgcolor: PRIMARY, opacity: 0.5 },
};

function idEq(a, b) {
  return a != null && b != null && (a === b || String(a) === String(b));
}

/**
 * Assign DBA to a user — shared across merchant list pages.
 */
export default function DbaAssignUserDialog({
  open,
  onClose,
  onAssign,
  selectedDba,
  usersList = [],
  value,
  onChange,
  processingId,
  title = 'Assign DBA to User',
  confirmLabel = 'Assign',
  description,
}) {
  const selected =
    usersList.find((u) => idEq(u.id, value) || idEq(u.uuid, value)) || null;
  const busy = processingId && selectedDba && idEq(processingId, selectedDba.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={dialogPaperSx}>
      <DialogTitle sx={titleSx}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
          {description ??
            `Assign ${selectedDba?.name || 'this DBA'} to a user. Search and select a user below.`}
        </DialogContentText>
        <Autocomplete
          options={usersList}
          getOptionLabel={(option) => option?.name || ''}
          value={selected}
          onChange={(_, newValue) => onChange(newValue?.id ?? newValue?.uuid ?? '')}
          isOptionEqualToValue={(opt, val) => idEq(opt?.id, val?.id) || idEq(opt?.uuid, val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search User"
              variant="outlined"
              placeholder="Type to search..."
              sx={{ bgcolor: 'white' }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id ?? option.uuid}>
              {option.name}
            </Box>
          )}
          sx={{ mt: 1 }}
          fullWidth
          clearOnEscape
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="secondary" sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          onClick={onAssign}
          variant="contained"
          color="primary"
          disabled={!value || busy}
          startIcon={busy ? <CircularProgress size={20} /> : null}
          sx={primaryBtnSx}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
